"use client";

import { decodeHtmlEntities, getGenericValueCase } from "@/utils/helper";
import {
  formatVendorAssessmentStatusForListDisplay,
  removeBracketsFromStatus,
} from "@/utils/activeProjectStatusUtils";
import { unwrapCountedObjects } from "@/components/ProjectsInArtwork/utils/artworkListApiTransform";
import { resolveIngredientComplianceFromResponseFragment } from "@/utils/vendor/normalizeVendorAssessmentResponse";
import type {
  VendorAssessmentListItem,
  VendorAssessmentListRequest,
  VendorAssessmentListResponse,
  VendorAssessmentVendorStatus,
} from "../types/vendorAssessmentListApi.types";

const VENDOR_ASSESSMENT_STATUS = "vendor assessment";

export function resolvePublishedFromIngredientStatus(
  raw: string | null | undefined
): boolean | null {
  const normalized = getGenericValueCase(raw);
  if (!normalized) return null;
  if (normalized === "ingredient approved") return true;
  if (normalized === "ingredient concern") return false;
  return null;
}

export function formatVendorAssessmentStatusLabel(status: string | null | undefined): string {
  return formatVendorAssessmentStatusForListDisplay(status);
}

export function isVendorAssessmentStatusValue(status: string | null | undefined): boolean {
  return getGenericValueCase(removeBracketsFromStatus(status)) === VENDOR_ASSESSMENT_STATUS;
}

function normalizeWorkflowStatus(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const statuses = raw.filter((s): s is string => typeof s === "string" && s.trim().length > 0);
  return statuses.length > 0 ? statuses : undefined;
}

export function resolveVendorPublished(
  vendor: VendorAssessmentVendorStatus | undefined,
  productSubStatus?: string | null
): boolean | null | undefined {
  if (!vendor) {
    return resolvePublishedFromIngredientStatus(productSubStatus);
  }
  if (vendor.published === true || vendor.published === false) return vendor.published;
  return (
    resolvePublishedFromIngredientStatus(vendor.ingredientStatus) ??
    resolvePublishedFromIngredientStatus(vendor.subStatus) ??
    resolvePublishedFromIngredientStatus(productSubStatus)
  );
}

function resolveIngredientComplianceFromVendorFragments(
  vendor: Record<string, unknown>,
  assessment: Record<string, unknown> | null
): string {
  return (
    resolveIngredientComplianceFromResponseFragment(assessment) ??
    resolveIngredientComplianceFromResponseFragment(vendor) ??
    resolveIngredientComplianceFromResponseFragment((vendor as any).vendorIngredientDetails) ??
    resolveIngredientComplianceFromResponseFragment((assessment as any)?.vendorIngredientDetails) ??
    ""
  );
}

const toNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

function str(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return String(v).trim();
  }
  return "";
}

function pickPrimaryAssessment(vendor: Record<string, unknown>): Record<string, unknown> | null {
  const assessments = unwrapCountedObjects<Record<string, unknown>>((vendor as any).vendorAssessment);
  return assessments[0] ?? null;
}

/** `vendorList` scalars may be plain strings or `{ value|name|id }` objects (Ready For Orders API shape). */
function pickVendorListScalar(raw: unknown): string {
  if (raw == null) return "";
  if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
    return str(raw);
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    for (const k of ["value", "name", "description"] as const) {
      const v = o[k];
      if (v != null && str(v)) return str(v);
    }
    if (o.id != null && str(o.id)) return str(o.id);
  }
  return "";
}

function formatManufacturingFacilityLine(f: Record<string, unknown>): string {
  const name = str(f.name);
  const city = str(f.city);
  const state = str(f.state);
  const tail = [city, state].filter(Boolean).join(", ");
  if (name && tail) return `${name} (${tail})`;
  return name || tail;
}

function formatManufacturingFacilityLocationsFromApi(raw: unknown): string {
  if (raw == null) return "";
  if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
    return str(raw);
  }
  const facilityObjs = unwrapCountedObjects<Record<string, unknown>>(raw);
  return facilityObjs.map((f) => formatManufacturingFacilityLine(f)).filter(Boolean).join(", ");
}

function resolveIngredientStatusForVendorItem(
  vendor: Record<string, unknown>,
  assessment: Record<string, unknown> | null,
  productSubStatus?: string
): string {
  // List API: `subStatus` (e.g. Ingredient_Approved) on idea or nested vendor/assessment rows.
  const direct =
    str(vendor.ingredientStatus) ||
    str(assessment?.ingredientStatus) ||
    str(vendor.subStatus) ||
    str(assessment?.subStatus) ||
    str(productSubStatus) ||
    "";
  if (direct) return direct;
  return resolveIngredientComplianceFromVendorFragments(vendor, assessment);
}

function extractVendorPanelFields(
  vendor: Record<string, unknown>,
  assessment: Record<string, unknown> | null
): Pick<
  VendorAssessmentVendorStatus,
  | "typeOfVendor"
  | "typeOfPackage"
  | "retailStructure"
  | "logistics"
  | "manufacturingFacilityLocations"
  | "shelfLife"
  | "proposedRetail"
  | "size"
  | "packSize"
  | "howManyFacilities"
  | "reasonForDevelopment"
> {
  const typeOfVendor = str(vendor.typeOfVendor ?? assessment?.typeOfVendor);
  const typeOfPackage = str(vendor.typeOfPackage ?? assessment?.typeOfPackage);
  const retailStructure =
    pickVendorListScalar(vendor.retailStructure) ||
    pickVendorListScalar(assessment?.retailStructure);
  const logistics =
    pickVendorListScalar(vendor.vendorLogistics) ||
    pickVendorListScalar(vendor.vendor_logistics) ||
    pickVendorListScalar(vendor.logistics) ||
    pickVendorListScalar(assessment?.logistics);
  const manufacturingFacilityLocations = formatManufacturingFacilityLocationsFromApi(
    vendor.manufacturingFacilityLocations ??
      assessment?.manufacturingFacilityLocations ??
      vendor.facilitiesLocations ??
      assessment?.facilitiesLocations
  );
  const shelfLife =
    pickVendorListScalar(vendor.shelfLife) || pickVendorListScalar(assessment?.shelfLife);
  const proposedRetail =
    pickVendorListScalar(vendor.proposedRetail) ||
    pickVendorListScalar(assessment?.proposedRetail) ||
    pickVendorListScalar(vendor.retailPrice) ||
    pickVendorListScalar(assessment?.retailPrice);
  const size = pickVendorListScalar(vendor.size) || pickVendorListScalar(assessment?.size);
  const packSize =
    pickVendorListScalar(vendor.packSize) || pickVendorListScalar(assessment?.packSize);
  const howManyFacilities =
    pickVendorListScalar(vendor.howManyFacilities) ||
    pickVendorListScalar(assessment?.howManyFacilities);
  const reasonForDevelopment =
    pickVendorListScalar(vendor.reasonForDevelopment) ||
    pickVendorListScalar(assessment?.reasonForDevelopment);

  return {
    typeOfVendor: typeOfVendor || undefined,
    typeOfPackage: typeOfPackage || undefined,
    retailStructure: retailStructure || undefined,
    logistics: logistics || undefined,
    manufacturingFacilityLocations: manufacturingFacilityLocations || undefined,
    shelfLife: shelfLife || undefined,
    proposedRetail: proposedRetail || undefined,
    size: size || undefined,
    packSize: packSize || undefined,
    howManyFacilities: howManyFacilities || undefined,
    reasonForDevelopment: reasonForDevelopment || undefined,
  };
}

function mapVendorStatusEntry(
  entry: {
    vendorId: unknown;
    vendorName: string;
    vendorAssessmentStatus: string;
    businessStatus: string;
    ingredientStatus: string;
    previousState: string;
    vendorAssessmentId: string;
    workflowStatus?: string[];
    typeOfVendor?: string;
    typeOfPackage?: string;
    retailStructure?: string;
    logistics?: string;
    manufacturingFacilityLocations?: string;
    shelfLife?: string;
    proposedRetail?: string;
    size?: string;
    packSize?: string;
    howManyFacilities?: string;
    reasonForDevelopment?: string;
  }
): VendorAssessmentVendorStatus {
  const published = resolvePublishedFromIngredientStatus(entry.ingredientStatus);
  return {
    id: entry.vendorId as string | number | undefined,
    vendorName: decodeHtmlEntities(entry.vendorName),
    vendorAssessmentStatus: entry.vendorAssessmentStatus || undefined,
    businessStatus: entry.businessStatus || undefined,
    ingredientStatus: entry.ingredientStatus || undefined,
    subStatus: entry.ingredientStatus || undefined,
    published,
    previousState: entry.previousState || undefined,
    vendorAssessmentId: entry.vendorAssessmentId || undefined,
    workflowStatus: entry.workflowStatus,
    typeOfVendor: entry.typeOfVendor,
    typeOfPackage: entry.typeOfPackage,
    retailStructure: entry.retailStructure,
    logistics: entry.logistics,
    manufacturingFacilityLocations: entry.manufacturingFacilityLocations,
    shelfLife: entry.shelfLife,
    proposedRetail: entry.proposedRetail,
    size: entry.size,
    packSize: entry.packSize,
    howManyFacilities: entry.howManyFacilities,
    reasonForDevelopment: entry.reasonForDevelopment,
  };
}

function flattenLegacyVendorStatus(
  raw: unknown,
  productSubStatus?: string
): VendorAssessmentVendorStatus[] {
  if (!Array.isArray(raw)) return [];
  const out: VendorAssessmentVendorStatus[] = [];

  for (const item of raw) {
    if (item == null || typeof item !== "object") continue;
    const v = item as Record<string, unknown>;
    const panelFields = extractVendorPanelFields(v, null);
    out.push(
      mapVendorStatusEntry({
        vendorId: v.id ?? v.vendorId,
        vendorName: str(v.vendorName ?? v.vendorCorporateName ?? v.name),
        vendorAssessmentStatus: str(v.vendorAssessmentStatus ?? v.businessStatus),
        businessStatus: str(v.businessStatus),
        ingredientStatus: resolveIngredientStatusForVendorItem(v, null, productSubStatus),
        previousState: str(v.previousState),
        vendorAssessmentId: str(v.vendorAssessmentId),
        workflowStatus: normalizeWorkflowStatus(v.workflowStatus),
        ...panelFields,
      })
    );
  }

  return out;
}

function flattenVendorListToVendorStatus(
  raw: unknown,
  productSubStatus?: string
): VendorAssessmentVendorStatus[] {
  const vendors = unwrapCountedObjects<Record<string, unknown>>(raw);
  const out: VendorAssessmentVendorStatus[] = [];

  for (const vendor of vendors) {
    const vendorId = (vendor as any).vendorId ?? (vendor as any).id;
    const vendorName = str(
      (vendor as any).vendorCorporateName ?? (vendor as any).vendorName ?? (vendor as any).name
    );
    const assessment = pickPrimaryAssessment(vendor);
    const vendorAssessmentStatus = str(
      assessment?.businessStatus ?? (vendor as any).businessStatus
    );
    const ingredientStatus = resolveIngredientStatusForVendorItem(vendor, assessment, productSubStatus);
    const panelFields = extractVendorPanelFields(vendor, assessment);

    out.push(
      mapVendorStatusEntry({
        vendorId,
        vendorName,
        vendorAssessmentStatus,
        businessStatus: str(assessment?.businessStatus ?? (vendor as any).businessStatus),
        ingredientStatus,
        previousState: str(assessment?.previousStateBeforeCancellation),
        vendorAssessmentId: str(assessment?.vendorAssessmentId),
        workflowStatus: normalizeWorkflowStatus(assessment?.workflowStatus),
        ...panelFields,
      })
    );
  }

  return out;
}

function buildVendorStatuses(vendorStatus: VendorAssessmentVendorStatus[], businessStatus?: string): string[] {
  if (vendorStatus.length > 1) {
    return vendorStatus
      .map((v) => str(v.vendorAssessmentStatus))
      .filter(Boolean);
  }
  if (businessStatus) return [businessStatus];
  if (vendorStatus.length === 1) {
    const s = str(vendorStatus[0]?.vendorAssessmentStatus);
    return s ? [s] : [];
  }
  return [];
}

export function normalizeVendorAssessmentListItem(
  raw: unknown,
  fallbackId: string
): VendorAssessmentListItem {
  const r = (raw != null && typeof raw === "object" && !Array.isArray(raw) ? raw : {}) as Record<
    string,
    unknown
  >;

  const idCandidate = r.id ?? r.ID ?? r.ideaId;
  const id: string | number =
    idCandidate != null && str(idCandidate) !== "" ? (idCandidate as string | number) : fallbackId;

  const title = str(r.title ?? r.name);
  const businessCategoryName = str(r.businessCategoryName ?? r.category);
  const categoryManager = str(r.categoryManager);
  const developer = str(r.developer);

  const productSubStatus = str(r.subStatus);

  const fromVendorList = flattenVendorListToVendorStatus(r.vendorList, productSubStatus);
  const ideaReasonForDevelopment = pickVendorListScalar(r.reasonForDevelopment);
  const vendorStatusRaw =
    fromVendorList.length > 0
      ? fromVendorList
      : flattenLegacyVendorStatus(r.vendorStatus ?? r.vendorDetails, productSubStatus);
  const vendorStatus = ideaReasonForDevelopment
    ? vendorStatusRaw.map((v) => ({
        ...v,
        reasonForDevelopment: v.reasonForDevelopment || ideaReasonForDevelopment,
      }))
    : vendorStatusRaw;
  const businessStatus = str(r.businessStatus) || undefined;
  const vendorStatuses = buildVendorStatuses(vendorStatus, businessStatus);
  const vendorCorporateName = vendorStatus
    .map((v) => decodeHtmlEntities(str(v.vendorName)))
    .filter(Boolean);
  const vendorDetails = vendorStatus.map((v) => ({
    id: v.id,
    vendorName: v.vendorName,
    vendorAssessmentId: v.vendorAssessmentId,
    ingredientStatus: v.ingredientStatus,
    subStatus: v.subStatus,
    published: v.published,
    vendorAssessmentStatus: v.vendorAssessmentStatus,
    typeOfVendor: v.typeOfVendor,
    typeOfPackage: v.typeOfPackage,
    retailStructure: v.retailStructure,
    logistics: v.logistics,
    manufacturingFacilityLocations: v.manufacturingFacilityLocations,
    shelfLife: v.shelfLife,
    proposedRetail: v.proposedRetail,
    size: v.size,
    packSize: v.packSize,
    howManyFacilities: v.howManyFacilities,
    reasonForDevelopment: v.reasonForDevelopment,
  }));

  return {
    ...r,
    id,
    title,
    name: title,
    businessCategoryName,
    category: businessCategoryName,
    businessStatus,
    subStatus: productSubStatus || null,
    developer,
    categoryManager,
    categoryManagerUser: categoryManager
      ? { userName: categoryManager.trim() }
      : undefined,
    vendorStatus,
    vendorStatuses,
    vendorCorporateName,
    vendorDetails,
    sellingPeriod: str(r.sellingPeriod) || undefined,
    productType: str(r.productType) || undefined,
    // Idea-level size / packSize / retailPrice for list columns (vendor/assessment mappings left unchanged).
    size: pickVendorListScalar(r.size) || undefined,
    packSize: pickVendorListScalar(r.packSize) || undefined,
    retailPrice: pickVendorListScalar(r.retailPrice) || undefined,
    reasonForDevelopment: ideaReasonForDevelopment || undefined,
    productSpecificationType: str(r.productSpecificationType) || undefined,
    addedOn: str(r.addedOn) || undefined,
    cancellationDate:
      r.cancellationDate == null ? r.cancellationDate ?? null : str(r.cancellationDate) || null,
  };
}

export function normalizeVendorAssessmentListResponse(
  raw: unknown,
  req: VendorAssessmentListRequest,
  totalFromHeader?: unknown
): VendorAssessmentListResponse {
  if (Array.isArray(raw)) {
    const headerTotal = toNumber(totalFromHeader);
    return {
      items: raw.map((it, idx) => normalizeVendorAssessmentListItem(it, `${req.pageNo}-${idx}`)),
      totalCount: headerTotal ?? raw.length,
      pageNo: req.pageNo,
      pageSize: req.pageSize,
    };
  }

  const rawAsUnknown = (raw != null && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const itemsContainer =
    rawAsUnknown.items ??
    rawAsUnknown.data ??
    rawAsUnknown.content ??
    rawAsUnknown.objects ??
    [];
  const unwrappedItems = unwrapCountedObjects(itemsContainer);

  const headerTotal = toNumber(totalFromHeader);
  const bodyTotal =
    toNumber(rawAsUnknown.totalCount) ??
    toNumber(rawAsUnknown.totalElements) ??
    toNumber(rawAsUnknown.totalRecords) ??
    toNumber(rawAsUnknown.count);

  const normalizedItems = unwrappedItems.map((it, idx) =>
    normalizeVendorAssessmentListItem(it, `${req.pageNo}-${idx}`)
  );

  return {
    items: normalizedItems,
    totalCount: headerTotal ?? bodyTotal ?? normalizedItems.length,
    pageNo: toNumber(rawAsUnknown.pageNo) ?? toNumber(rawAsUnknown.pageNumber) ?? req.pageNo,
    pageSize: toNumber(rawAsUnknown.pageSize) ?? toNumber(rawAsUnknown.size) ?? req.pageSize,
  };
}
